import type { TFunction } from 'i18next';
import { ApiError } from '@d-orca/shared/client/apis';
import type { IToast } from '@components/ui/Toasts/useToasts';

/** The upload refusals the route answers itself, by envelope code. */
const REFUSAL_REASONS: Readonly<Record<string, string>> = {
  INVALID_FILE_FORMAT: 'wrongFormat',
  FILE_TOO_LARGE: 'fileTooLarge',
};

/** A parse failure's `details.reason` (the adapters' coarse reasons); every
 *  other reason — malformed, an unknown format, a missing header — reads as
 *  "not a schedule export". */
const PARSE_REASONS: Readonly<Record<string, string>> = {
  'too-large': 'tooManyActivities',
  'no-activities': 'noActivities',
};

function reasonKey(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  if (error.code === 'SCHEDULE_IMPORT_PARSE_FAILED') return PARSE_REASONS[String(error.details.reason)] ?? 'unreadable';
  return REFUSAL_REASONS[String(error.code)];
}

/**
 * The toast for a failed schedule-file import: what failed, and — when the
 * server said — why (the wrong format, too big, no activities, unreadable), so
 * the reader knows whether another file or another export will do. One
 * wording for every surface that imports a file (the Scheduler, the top bar's
 * picker elsewhere).
 */
export function importFileFailureToast(t: TFunction, error: unknown): Omit<IToast, 'id'> {
  const reason = reasonKey(error);
  return {
    kind: 'error',
    title: t('scheduler.import.fileFailed'),
    ...(reason ? { sub: t(`scheduler.import.fileReason.${reason}`) } : {}),
  };
}
